"use client"

import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ArrowDownUp, Copy, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type EncodingMode = "component" | "full" | "form-data"

interface QueryParam {
  id: string
  key: string
  value: string
}

export function UrlEncoder() {
  const [inputText, setInputText] = useState("")
  const [outputText, setOutputText] = useState("")
  const [mode, setMode] = useState<"encode" | "decode">("encode")
  const [encodingMode, setEncodingMode] = useState<EncodingMode>("component")
  const [activeTab, setActiveTab] = useState("encoder")
  const [queryParams, setQueryParams] = useState<QueryParam[]>([
    { id: "1", key: "", value: "" },
  ])
  const [baseUrl, setBaseUrl] = useState("")
  const [builtUrl, setBuiltUrl] = useState("")

  const encode = useCallback((text: string, encMode: EncodingMode): string => {
    switch (encMode) {
      case "component":
        return encodeURIComponent(text)
      case "full":
        return encodeURI(text)
      case "form-data":
        return encodeURIComponent(text).replace(/%20/g, "+")
      default:
        return encodeURIComponent(text)
    }
  }, [])

  const decode = useCallback((text: string, encMode: EncodingMode): string => {
    switch (encMode) {
      case "component":
        return decodeURIComponent(text)
      case "full":
        return decodeURI(text)
      case "form-data":
        return decodeURIComponent(text.replace(/\+/g, "%20"))
      default:
        return decodeURIComponent(text)
    }
  }, [])

  const handleProcess = () => {
    try {
      if (mode === "encode") {
        setOutputText(encode(inputText, encodingMode))
        toast.success("URL encoded successfully")
      } else {
        setOutputText(decode(inputText, encodingMode))
        toast.success("URL decoded successfully")
      }
    } catch (error) {
      toast.error(`${mode === "encode" ? "Encoding" : "Decoding"} failed: ${error instanceof Error ? error.message : "Invalid input"}`)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(outputText)
    toast.success("Copied to clipboard!")
  }

  const toggleMode = () => {
    setMode(mode === "encode" ? "decode" : "encode")
    setInputText(outputText)
    setOutputText("")
  }

  const addParam = () => {
    setQueryParams([...queryParams, { id: Date.now().toString(), key: "", value: "" }])
  }

  const removeParam = (id: string) => {
    if (queryParams.length <= 1) return
    setQueryParams(queryParams.filter((p) => p.id !== id))
  }

  const updateParam = (id: string, field: "key" | "value", newValue: string) => {
    setQueryParams(queryParams.map((p) => (p.id === id ? { ...p, [field]: newValue } : p)))
  }

  const buildUrl = () => {
    const validParams = queryParams.filter((p) => p.key.trim())
    if (!baseUrl.trim() && validParams.length === 0) {
      toast.error("Enter a base URL or at least one query parameter")
      return
    }

    const searchParams = new URLSearchParams()
    validParams.forEach((p) => searchParams.append(p.key, p.value))

    const queryString = searchParams.toString()
    const result = baseUrl.trim()
      ? `${baseUrl.trim()}${queryString ? `?${queryString}` : ""}`
      : `?${queryString}`

    setBuiltUrl(result)
    toast.success("URL built successfully")
  }

  const parseUrl = () => {
    if (!baseUrl.trim()) {
      toast.error("Enter a URL to parse")
      return
    }

    try {
      const url = new URL(baseUrl.startsWith("http") ? baseUrl : `https://${baseUrl}`)
      const params: QueryParam[] = []
      url.searchParams.forEach((value, key) => {
        params.push({ id: Date.now().toString() + Math.random(), key, value })
      })

      if (params.length > 0) {
        setQueryParams(params)
        setBaseUrl(`${url.origin}${url.pathname}`)
        toast.success(`Parsed ${params.length} query parameter${params.length !== 1 ? "s" : ""}`)
      } else {
        toast.info("No query parameters found in URL")
      }
    } catch {
      toast.error("Invalid URL format")
    }
  }

  const copyBuiltUrl = () => {
    navigator.clipboard.writeText(builtUrl)
    toast.success("Copied to clipboard!")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>URL Encoder/Decoder</CardTitle>
        <CardDescription>
          Encode or decode URL components, build query strings, and parse URLs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="encoder">Encode / Decode</TabsTrigger>
            <TabsTrigger value="query-builder">Query Builder</TabsTrigger>
          </TabsList>

          <TabsContent value="encoder" className="space-y-4 pt-2">
            <div className="flex flex-col sm:flex-row gap-4">
              <Tabs value={mode} onValueChange={(value) => setMode(value as "encode" | "decode")} className="flex-1">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="encode">Encode</TabsTrigger>
                  <TabsTrigger value="decode">Decode</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="sm:w-48">
                <Select value={encodingMode} onValueChange={(v) => setEncodingMode(v as EncodingMode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="component">Component</SelectItem>
                    <SelectItem value="full">Full URL</SelectItem>
                    <SelectItem value="form-data">Form Data (+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {encodingMode === "component" && "encodeURIComponent — encodes all special characters including : / ? # @ etc."}
              {encodingMode === "full" && "encodeURI — preserves URL structure characters (: / ? # @ etc.)"}
              {encodingMode === "form-data" && "Form data encoding — same as component but spaces become + instead of %20"}
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Input</Label>
                <Textarea
                  placeholder={mode === "encode" ? "Enter text to encode" : "Enter URL encoded text to decode"}
                  className="min-h-[150px] md:min-h-[200px] font-mono text-sm"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
                <Button variant="outline" size="sm" onClick={() => setInputText("")}>
                  Clear
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Output</Label>
                <Textarea
                  placeholder="Result will appear here"
                  className="min-h-[150px] md:min-h-[200px] font-mono text-sm"
                  value={outputText}
                  readOnly
                />
                <Button variant="outline" size="sm" onClick={handleCopy} disabled={!outputText}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </div>

            <div className="flex justify-center gap-4 pt-4">
              <Button onClick={handleProcess} disabled={!inputText}>
                {mode === "encode" ? "Encode" : "Decode"}
              </Button>
              <Button variant="outline" onClick={toggleMode} disabled={!outputText}>
                <ArrowDownUp className="h-4 w-4 mr-2" />
                Swap
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="query-builder" className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Base URL</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/api/data"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  className="font-mono text-sm"
                />
                <Button variant="outline" size="sm" onClick={parseUrl}>
                  Parse
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Query Parameters</Label>
                <Button variant="outline" size="sm" onClick={addParam}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              {queryParams.map((param) => (
                <div key={param.id} className="flex gap-2 items-center">
                  <Input
                    placeholder="Key"
                    value={param.key}
                    onChange={(e) => updateParam(param.id, "key", e.target.value)}
                    className="font-mono text-sm"
                  />
                  <span className="text-muted-foreground">=</span>
                  <Input
                    placeholder="Value"
                    value={param.value}
                    onChange={(e) => updateParam(param.id, "value", e.target.value)}
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeParam(param.id)}
                    disabled={queryParams.length <= 1}
                    className="flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button onClick={buildUrl} className="w-full">
              Build URL
            </Button>

            {builtUrl && (
              <div className="space-y-2">
                <Label>Result</Label>
                <div className="flex gap-2">
                  <Input
                    value={builtUrl}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" size="icon" onClick={copyBuiltUrl}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
